import clsx from "clsx";
import { FC } from "react";

import { NextPreviousNav } from "@tsmono/react/components";

import { ApplicationNavbar } from "../navbar/ApplicationNavbar";

import styles from "./SampleNavbar.module.css";

export interface SampleNavigationConfig {
  onPrevious: () => void;
  onNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
  /** Where prev/next go, so the chevrons can be real links. */
  previousHref?: string;
  nextHref?: string;
}

export interface NavbarConfig {
  currentPath: string | undefined;
  fnNavigationUrl: (file: string, log_dir?: string) => string;
  /** Override the back button's target (default: parent of currentPath). */
  backUrl?: string;
  /** Override the home button's target (default: root listing). */
  homeUrl?: string;
  bordered?: boolean;
  breadcrumbsEnabled?: boolean;
}

interface SampleNavbarProps {
  sampleId: string | undefined;
  epoch: string | undefined;
  navigation: SampleNavigationConfig;
  navbarConfig: NavbarConfig;
  /** Extra loading signal for the navbar's activity bar (e.g. this sample
   *  still loading), ORed with the selected log's own loading state. */
  loading?: boolean;
}

/**
 * The global header bar shown above a sample's content: the breadcrumb /
 * theme / options chrome (ApplicationNavbar) plus the prev/next sample
 * controls. Shared by the sample detail view and the single-event focus page.
 */
export const SampleNavbar: FC<SampleNavbarProps> = ({
  sampleId,
  epoch,
  navigation,
  navbarConfig,
  loading,
}) => {
  const { onPrevious, onNext, hasPrevious, hasNext, previousHref, nextHref } =
    navigation;
  const {
    currentPath,
    fnNavigationUrl,
    backUrl,
    homeUrl,
    bordered = true,
    breadcrumbsEnabled,
  } = navbarConfig;

  return (
    <ApplicationNavbar
      currentPath={currentPath}
      fnNavigationUrl={fnNavigationUrl}
      backUrl={backUrl}
      homeUrl={homeUrl}
      bordered={bordered}
      breadcrumbsEnabled={breadcrumbsEnabled}
      loading={loading}
    >
      {/* The shared chevrons bind ArrowLeft/ArrowRight next to the
          "(←)"/"(→)" tooltips, so every surface that renders this navbar
          (sample detail, single-event focus page) steps samples the same way. */}
      <NextPreviousNav
        onPrevious={onPrevious}
        onNext={onNext}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
        previousHref={previousHref}
        nextHref={nextHref}
        previousTitle="Previous sample"
        nextTitle="Next sample"
      >
        <div className={clsx(styles.sampleInfo, "text-size-smallest")}>
          Sample {sampleId} (Epoch {epoch})
        </div>
      </NextPreviousNav>
    </ApplicationNavbar>
  );
};
